import { v4 as uuidv4 } from 'uuid';

import { ArticleItem, ContentUnion, ExternalCollectionItem, PageModule, QueryItem } from 'data/generated/graphql';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { isArticleOrMediaContentType } from 'utils/contentType';
import { reportError } from 'utils/errorBoundary';

import { EntityType } from 'contexts/datamodel/DataModelContext.constants';

// Converts a raw Allesseh content response item to the ArticleItem shape used by the data model.
// Shared between layout query preview, page query preview, and any other consumer that needs
// to map Allesseh search results into the internal entity format.
export const allessehContentToArticleItem = (content: AllessehContent): ArticleItem => {
  const {
    id: originId,
    attributes: {
      product,
      seo_id: seoId,
      canonical_url: canonicalUrl,
      source_url: sourceUrl,
      content_status: status,
      embargo_datetime_utc: embargoDatetimeUtc
    }
  } = content.data;

  let originContentType = content.data.type;
  if (!isArticleOrMediaContentType(originContentType)) {
    if (originContentType === 'SummaryModel') {
      originContentType = content.data.attributes.type;
    } else {
      originContentType = 'article';
      reportError(`Unknown origin content type: ${content.data.type} for ${originId}, falling back to article`);
    }
  }

  return {
    type: EntityType.Article as 'Article',
    uuid: uuidv4(),
    content: JSON.stringify(content),
    attributes: {
      base_doc_id: originId,
      hosted_url: canonicalUrl || sourceUrl,
      id: originId,
      link: originContentType === 'commerceproduct' ? '' : `api/articles/v1/originid/${originId}`,
      originContentType,
      originId,
      product,
      seo_id: originContentType === 'commerceproduct' ? '' : seoId,
      status,
      embargoDatetimeUtc
    }
  };
};

// Converts a PageModule's moduleItems into the ContentUnion[] shape expected by the
// page-editor data model. Handles query items and Allesseh collection IDs; all other
// item types are dropped.
export const convertPageModuleItemsToDataModelEntities = (module: PageModule): ContentUnion[] =>
  module.moduleItems
    .map((moduleItem) => {
      if (moduleItem.itemFields.queryItem) {
        const queryItem: QueryItem = {
          type: 'Query',
          uuid: uuidv4(),
          attributes: {
            id: uuidv4(),
            isFeed: moduleItem.itemFields.queryItem.isFeed,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            query: JSON.parse(moduleItem.itemFields.queryItem.jsonQuery),
            feedTitle: moduleItem.itemFields.queryItem.title,
            feedUrl: moduleItem.itemFields.queryItem.url
          }
        };
        return queryItem;
      }
      if (moduleItem.itemFields.allessehCollectionItem) {
        const allessehCollectionItem: ExternalCollectionItem = {
          type: 'Collection',
          uuid: uuidv4(),
          attributes: {
            id: moduleItem.itemFields.allessehCollectionItem.allessehCollectionId,
            repo: 'Allesseh',
            limit: 5
          },
          contentItems: [],
          metadata: {
            id: moduleItem.itemFields.allessehCollectionItem.allessehCollectionId,
            name: `Allesseh Collection ${moduleItem.itemFields.allessehCollectionItem.allessehCollectionId}`,
            createdUtc: 0,
            creatorUser: { __typename: undefined, djUserId: '' },
            isArchived: false,
            notes: '',
            publicationKey: '',
            revisorUser: { __typename: undefined, djUserId: '' },
            updatedUtc: 0
          }
        };
        return allessehCollectionItem;
      }
      return null;
    })
    .filter((item) => item !== null) as ContentUnion[];
